import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Loader2,
  Plus,
  Trash2,
  Archive,
  ArchiveRestore,
  KeyRound,
  Copy,
} from "lucide-react";

export default function LeadSourcesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", type: "manual" });
  const [tokenModal, setTokenModal] = useState(null); // { token, endpoint_url, curl_example, source_name }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/lead-sources");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    if (!form.name.trim()) return;
    try {
      await api.post("/lead-sources", form);
      toast.success("Source créée");
      setOpen(false);
      setForm({ name: "", type: "manual" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const archive = async (s) => {
    if (!window.confirm(`Archiver la source « ${s.name} » ? Elle disparaîtra du dropdown de création mais ses leads restent visibles.`)) return;
    try {
      await api.patch(`/lead-sources/${s.id}`, { active: false });
      toast.success("Source archivée");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const restore = async (s) => {
    try {
      await api.patch(`/lead-sources/${s.id}`, { active: true });
      toast.success("Source restaurée");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const generateToken = async (s) => {
    const hasToken = s.type === "webhook";
    if (!hasToken) return;
    const msg = "Générer un nouveau token invalidera l'ancien (rotation). Continuer ?";
    // Si aucun token n'a jamais été généré, on affiche un message plus doux
    if (!window.confirm(msg)) return;
    try {
      const { data } = await api.post(`/lead-sources/${s.id}/regenerate-token`);
      setTokenModal({ ...data, source_name: s.name });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copié`);
    } catch {
      toast.error("Copie impossible");
    }
  };

  const remove = async (s) => {
    if (s.leads_count > 0) {
      // Sécurité UX : le bouton n'est pas censé être visible dans ce cas,
      // mais on refuse ici aussi pour cohérence avec le 409 côté API.
      toast.error(
        `${s.leads_count} lead(s) rattaché(s) — archivez la source à la place.`,
      );
      return;
    }
    if (!window.confirm(`Supprimer définitivement la source « ${s.name} » ? Cette action est irréversible.`)) return;
    try {
      await api.delete(`/lead-sources/${s.id}`);
      toast.success("Source supprimée");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const visible = showArchived ? items : items.filter((s) => s.active);
  const archivedCount = items.filter((s) => !s.active).length;

  return (
    <div className="space-y-6" data-testid="lead-sources-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Sources de leads</h1>
          <p className="text-sm text-slate-400">
            Configurez les provenances de vos prospects. L&apos;ingestion automatique via webhook sera activée prochainement.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Switch
              id="show-archived"
              checked={showArchived}
              onCheckedChange={setShowArchived}
              data-testid="toggle-archived"
            />
            <Label htmlFor="show-archived" className="cursor-pointer text-xs text-slate-400">
              Afficher archivées ({archivedCount})
            </Label>
          </div>
          <Button
            onClick={() => setOpen(true)}
            className="bg-[#C4553B] text-white hover:bg-[#A8402C]"
            data-testid="new-source-btn"
          >
            <Plus className="mr-1 h-4 w-4" /> Nouvelle source
          </Button>
        </div>
      </div>

      <Card className="border-slate-800 bg-slate-900/40">
        <CardContent className="overflow-x-auto p-0">
          {loading ? (
            <div className="p-6">
              <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
            </div>
          ) : visible.length === 0 ? (
            <p className="p-6 text-sm text-slate-500">
              {items.length === 0
                ? "Aucune source — commencez par en créer une."
                : "Aucune source active. Activez le toggle pour voir les archivées."}
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-slate-800 text-slate-400">
                <tr>
                  <th className="p-3 text-left">Nom</th>
                  <th className="p-3 text-left">Type</th>
                  <th className="p-3 text-left">Statut</th>
                  <th className="p-3 text-right">Leads reçus</th>
                  <th className="p-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((s) => {
                  const canHardDelete = s.leads_count === 0;
                  return (
                    <tr
                      key={s.id}
                      className={`border-b border-slate-800/60 last:border-0 ${!s.active ? "opacity-60" : ""}`}
                      data-testid={`source-row-${s.id}`}
                    >
                      <td className="p-3 font-medium">
                        {s.name}
                        {!s.active && (
                          <Badge
                            variant="outline"
                            className="ml-2 border-slate-700 bg-slate-800/50 text-[10px] text-slate-500"
                          >
                            Archivée
                          </Badge>
                        )}
                      </td>
                      <td className="p-3 text-slate-400">
                        <Badge variant="outline" className="border-slate-600 bg-slate-800 text-xs">
                          {s.type === "webhook" ? "Webhook (bientôt)" : "Saisie manuelle"}
                        </Badge>
                      </td>
                      <td className="p-3">
                        <Badge
                          variant="outline"
                          className={
                            s.active
                              ? "border-[#7A8450]/50 bg-[#7A8450]/10 text-[#B8C784] text-xs"
                              : "border-slate-700 bg-slate-800/40 text-slate-500 text-xs"
                          }
                          data-testid={`source-status-${s.id}`}
                        >
                          {s.active ? "Active" : "Archivée"}
                        </Badge>
                      </td>
                      <td className="p-3 text-right tabular" data-testid={`source-count-${s.id}`}>
                        {s.leads_count ?? 0}
                      </td>
                      <td className="p-3">
                        <div className="flex justify-end gap-1">
                          {s.type === "webhook" && s.active && (
                            <button
                              onClick={() => generateToken(s)}
                              className="rounded p-1.5 text-slate-400 hover:bg-[#C4553B]/10 hover:text-[#C4553B]"
                              data-testid={`gen-token-${s.id}`}
                              title="Générer / régénérer le token webhook"
                              aria-label="Générer token"
                            >
                              <KeyRound className="h-4 w-4" />
                            </button>
                          )}
                          {s.active ? (
                            <button
                              onClick={() => archive(s)}
                              className="rounded p-1.5 text-slate-400 hover:bg-slate-700/40 hover:text-slate-200"
                              data-testid={`archive-source-${s.id}`}
                              title="Archiver (soft delete — préserve les leads)"
                              aria-label="Archiver"
                            >
                              <Archive className="h-4 w-4" />
                            </button>
                          ) : (
                            <button
                              onClick={() => restore(s)}
                              className="rounded p-1.5 text-slate-400 hover:bg-slate-700/40 hover:text-slate-200"
                              data-testid={`restore-source-${s.id}`}
                              title="Restaurer la source"
                              aria-label="Restaurer"
                            >
                              <ArchiveRestore className="h-4 w-4" />
                            </button>
                          )}
                          {canHardDelete && (
                            <button
                              onClick={() => remove(s)}
                              className="rounded p-1.5 text-slate-400 hover:bg-red-500/10 hover:text-[#E67B6D]"
                              data-testid={`delete-source-${s.id}`}
                              title="Supprimer définitivement (aucun lead rattaché)"
                              aria-label="Supprimer définitivement"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-slate-800 bg-slate-900 text-slate-100">
          <DialogHeader>
            <DialogTitle>Nouvelle source de leads</DialogTitle>
            <DialogDescription className="text-slate-400">
              Indiquez la provenance (ex.&nbsp;«&nbsp;BOBEX&nbsp;», «&nbsp;Site web&nbsp;», «&nbsp;Salon habitat 2026&nbsp;»).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nom *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1 border-slate-700 bg-slate-950/40"
                data-testid="source-name-input"
              />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className="mt-1 border-slate-700 bg-slate-950/40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-slate-700 bg-slate-900">
                  <SelectItem value="manual">Saisie manuelle</SelectItem>
                  <SelectItem value="webhook">Webhook (bientôt disponible)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.type === "webhook" && (
              <p className="text-xs text-[#D99A2B]">
                Le token d&apos;ingestion sera généré et affiché en une fois lors de son activation.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              className="border-slate-700 bg-slate-800 text-slate-200"
            >
              Annuler
            </Button>
            <Button
              onClick={create}
              disabled={!form.name.trim()}
              className="bg-[#C4553B] text-white hover:bg-[#A8402C]"
              data-testid="source-submit"
            >
              Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!tokenModal} onOpenChange={(o) => !o && setTokenModal(null)}>
        <DialogContent className="border-slate-800 bg-slate-900 text-slate-100 sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Token webhook — {tokenModal?.source_name}</DialogTitle>
            <DialogDescription className="text-slate-400">
              Ce token est affiché <b>UNE SEULE FOIS</b>. Copiez-le maintenant, il ne pourra pas être ré-affiché. Régénérez-le si perdu.
            </DialogDescription>
          </DialogHeader>
          {tokenModal && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs text-slate-400">Token (secret)</Label>
                <div className="mt-1 flex items-center gap-2">
                  <code className="flex-1 break-all rounded border border-[#C4553B]/40 bg-slate-950 p-2 text-xs text-[#F0C572]" data-testid="token-value">
                    {tokenModal.token}
                  </code>
                  <button
                    onClick={() => copyToClipboard(tokenModal.token, "Token")}
                    className="rounded border border-slate-700 bg-slate-800 px-2 py-2 text-slate-300 hover:bg-slate-700"
                    data-testid="copy-token"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-400">Endpoint d&apos;ingestion</Label>
                <div className="mt-1 flex items-center gap-2">
                  <code className="flex-1 break-all rounded border border-slate-700 bg-slate-950 p-2 text-xs text-slate-300">
                    {tokenModal.endpoint_url}
                  </code>
                  <button
                    onClick={() => copyToClipboard(tokenModal.endpoint_url, "URL")}
                    className="rounded border border-slate-700 bg-slate-800 px-2 py-2 text-slate-300 hover:bg-slate-700"
                    data-testid="copy-url"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-400">Exemple curl</Label>
                <pre className="mt-1 max-h-48 overflow-auto rounded border border-slate-700 bg-slate-950 p-2 text-[11px] text-slate-300 whitespace-pre-wrap">
{tokenModal.curl_example}
                </pre>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              onClick={() => setTokenModal(null)}
              className="bg-[#C4553B] text-white hover:bg-[#A8402C]"
              data-testid="token-modal-close"
            >
              J&apos;ai copié le token
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
